import { useMemo, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateAverageReactionTime,
  calculateTotalTime,
  calculateSelectionAccuracy,
  calculateErrorCount,
} from "../shared/gameMetrics";

import "./RepeatedWords.css";

const WORDS = [
  "COMEDOR",
  "COCINA",
  "SILLA",
  "SOFÁ",
  "LÁMPARA",
  "MESA",
  "MESILLA",
  "MECEDORA",
  "SALÓN",
  "SOMBRILLA",
  "SOMBRERO",
  "LABERINTO",
  "LAGARTIJA",
  "LEÓN",
  "DELFÍN",
  "LEOPARDO",
  "PELOTA",
  "PESETA",
  "CASETA",
  "ROSETA",
  "APARTAR",
  "PITAR",
  "PASEAR",
  "ALIVIAR",
  "CAMINAR",
  "BAILARÍN",
  "FLAUTÍN",
  "COMODÍN",
  "MOCASÍN",
  "VIOLÍN",
  "CAMIÓN",
  "GORRIÓN",
  "MARRÓN",
  "SALMÓN",
  "BALÓN",
  "BASTA",
  "SUBASTA",
  "PASTA",
  "CASTA",
  "CANASTA",
  "PERFIL",
  "MANDIL",
  "SENIL",
  "MANDRIL",
  "PERNIL",
  "ABRIL",
  "SABER",
  "AMANECER",
  "CRECER",
  "HACER",
  "FAVORECER",
  "NACER",
  "GABARDINA",
  "SUPINA",
  "SOBRINA",
  "CORTINA",
  "SARDINA",
  "MORCILLA",
  "ALMANSA",
  "SALSA",
  "CASA",
  "PASA",
  "MASA",
  "CARCASA",
  "COCHE",
  "PARCHE",
  "PONCHE",
  "CACHÉ",
  "ESTUCHE",
  "REMACHE",
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 6,
    repeatedGroups: 1,
    repetitionsPerGroup: 2,
    columns: 2,
  },

  medium: {
    label: "Media",
    totalRounds: 10,
    itemCount: 10,
    repeatedGroups: 2,
    repetitionsPerGroup: 2,
    columns: 3,
  },

  hard: {
    label: "Difícil",
    totalRounds: 12,
    itemCount: 15,
    repeatedGroups: 3,
    repetitionsPerGroup: 3,
    columns: 3,
  },
};

function shuffle(list) {
  const copy = [...list];

  for (let index = copy.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(
      Math.random() * (index + 1)
    );

    [copy[index], copy[randomIndex]] = [
      copy[randomIndex],
      copy[index],
    ];
  }

  return copy;
}

function createRound(config) {
  const repeatedItemCount =
    config.repeatedGroups * config.repetitionsPerGroup;

  const uniqueItemCount =
    config.itemCount - repeatedItemCount;

  const safeUniqueItemCount = Math.max(
    uniqueItemCount,
    0
  );

  const requiredWords =
    config.repeatedGroups + safeUniqueItemCount;

  const selectedWords = shuffle(WORDS).slice(
    0,
    requiredWords
  );

  const repeatedWords = selectedWords.slice(
    0,
    config.repeatedGroups
  );

  const uniqueWords = selectedWords.slice(
    config.repeatedGroups
  );

  const repeatedItems = repeatedWords.flatMap(
    (word, groupIndex) =>
      Array.from(
        { length: config.repetitionsPerGroup },
        (_, repetitionIndex) => ({
          id: `repeated-${groupIndex}-${repetitionIndex}`,
          word,
          isRepeated: true,
        })
      )
  );

  const uniqueItems = uniqueWords.map(
    (word, index) => ({
      id: `unique-${index}`,
      word,
      isRepeated: false,
    })
  );

  return {
    repeatedWords,
    items: shuffle([
      ...repeatedItems,
      ...uniqueItems,
    ]),
  };
}

export default function RepeatedWords() {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState(null);

  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correctRounds, setCorrectRounds] =
    useState(0);

  const [correctSelections, setCorrectSelections] =
    useState(0);

  const [wrongSelections, setWrongSelections] =
    useState(0);

  const [omissions, setOmissions] = useState(0);

  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [roundStartTime, setRoundStartTime] =
    useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(
    () =>
      calculateProgress(
        roundIndex,
        totalRounds
      ),
    [roundIndex, totalRounds]
  );

  const totalErrors = calculateErrorCount(
    wrongSelections,
    omissions
  );

  const accuracy = calculateSelectionAccuracy({
    correctSelections,
    wrongSelections,
    omissions,
  });

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors: totalErrors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (
    difficultyOrConfig = selectedDifficulty || "easy"
  ) => {
    const {
      difficultyKey,
      config: nextConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    const now = Date.now();

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(createRound(nextConfig));
    setSelectedIds([]);

    setCorrectRounds(0);
    setCorrectSelections(0);
    setWrongSelections(0);
    setOmissions(0);
    setResponses([]);

    setStarted(true);
    setFinished(false);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
  };

  const toggleItem = (itemId) => {
    if (!started || finished) return;

    setSelectedIds((previous) =>
      previous.includes(itemId)
        ? previous.filter((id) => id !== itemId)
        : [...previous, itemId]
    );
  };

  const confirmRound = () => {
    if (!started || finished || !round || !config) {
      return;
    }

    const now = Date.now();

    const reactionTime =
      now - roundStartTime;

    const selectedItems = round.items.filter(
      (item) => selectedIds.includes(item.id)
    );

    const repeatedItems = round.items.filter(
      (item) => item.isRepeated
    );

    const roundCorrectSelections =
      selectedItems.filter(
        (item) => item.isRepeated
      ).length;

    const roundWrongSelections =
      selectedItems.filter(
        (item) => !item.isRepeated
      ).length;

    const roundOmissions =
      repeatedItems.length -
      roundCorrectSelections;

    const isPerfectRound =
      roundWrongSelections === 0 &&
      roundOmissions === 0;

    setResponses((previous) => [
      ...previous,
      {
        round: roundIndex + 1,
        repeatedWords: round.repeatedWords,
        selectedWords: selectedItems.map(
          (item) => item.word
        ),
        correctSelections: roundCorrectSelections,
        wrongSelections: roundWrongSelections,
        omissions: roundOmissions,
        isCorrect: isPerfectRound,
        reactionTime,
      },
    ]);

    if (isPerfectRound) {
      setCorrectRounds(
        (previous) => previous + 1
      );
    }

    setCorrectSelections(
      (previous) =>
        previous + roundCorrectSelections
    );

    setWrongSelections(
      (previous) =>
        previous + roundWrongSelections
    );

    setOmissions(
      (previous) =>
        previous + roundOmissions
    );

    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= config.totalRounds) {
      setStarted(false);
      setFinished(true);
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRoundIndex);
    setRound(createRound(config));
    setSelectedIds([]);
    setRoundStartTime(now);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);

    setRoundIndex(0);
    setRound(null);
    setSelectedIds([]);

    setStarted(false);
    setFinished(false);

    setCorrectRounds(0);
    setCorrectSelections(0);
    setWrongSelections(0);
    setOmissions(0);
    setResponses([]);

    setStartTime(null);
    setEndTime(null);
    setRoundStartTime(null);
  };

  if (
    !selectedDifficulty &&
    !started &&
    !finished
  ) {
    return (
      <GameLauncher
        title="Palabras repetidas"
        description="Observa todas las palabras y selecciona todas las que aparezcan repetidas."
        areas={[
          "Atención selectiva",
          "Atención sostenida",
          "Rastreo visual",
          "Lectura",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            max: 30,
            defaultValue: 10,
          }),

          GameFields.itemCount({
            label: "Cantidad de palabras",
            min: 4,
            max: 30,
            defaultValue: 10,
          }),

          GameFields.repeatedGroups({
            label: "Palabras diferentes repetidas",
            min: 1,
            max: 6,
            defaultValue: 2,
          }),

          GameFields.repetitionsPerGroup({
            label: "Veces que aparece cada palabra repetida",
            min: 2,
            max: 5,
            defaultValue: 2,
          }),

          GameFields.columns({
            min: 2,
            max: 5,
            defaultValue: 3,
          }),
        ]}
        onStart={startGame}
      />
    );
  }

  return (
    <div className="repeated-words-page">
      <div className="repeated-words-card">
        <header className="repeated-words-header">
          <div>
            <h1>Palabras repetidas</h1>

            <p>
              Dificultad:{" "}
              <strong>{config?.label}</strong>
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              startGame(selectedDifficulty)
            }
          >
            Reiniciar
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="repeated-words-instruction">
              <h2>
                Selecciona todas las palabras repetidas
              </h2>

              <p>
                Puede haber varias palabras diferentes
                repetidas.
              </p>
            </section>

            <section
              className="repeated-words-grid"
              style={{
                gridTemplateColumns: `repeat(${config.columns}, 1fr)`,
              }}
            >
              {round.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`repeated-word-item ${
                    selectedIds.includes(item.id)
                      ? "selected"
                      : ""
                  }`}
                  onClick={() =>
                    toggleItem(item.id)
                  }
                  aria-pressed={selectedIds.includes(
                    item.id
                  )}
                >
                  {item.word}
                </button>
              ))}
            </section>

            <button
              type="button"
              className="confirm-repeated-words-button"
              onClick={confirmRound}
              disabled={selectedIds.length === 0}
            >
              Confirmar selección
            </button>

            <section className="repeated-words-score">
              <span>
                Rondas correctas: {correctRounds}
              </span>

              <span>
                Selecciones correctas:{" "}
                {correctSelections}
              </span>

              <span>
                Errores: {wrongSelections}
              </span>

              <span>
                Omisiones: {omissions}
              </span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                {
                  label: "Rondas correctas",
                  value: correctRounds,
                },
                {
                  label: "Selecciones correctas",
                  value: correctSelections,
                },
                {
                  label: "Errores",
                  value: wrongSelections,
                },
                {
                  label: "Omisiones",
                  value: omissions,
                },
                {
                  label: "Precisión",
                  value: `${accuracy}%`,
                },
                {
                  label: "Tiempo medio",
                  value: `${averageReactionTime} ms`,
                },
                {
                  label: "Tiempo total",
                  value: `${totalTime}s`,
                },
              ]}
              onRepeat={() =>
                startGame(selectedDifficulty)
              }
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation
              interpretation={interpretation}
            />
          </>
        )}
      </div>
    </div>
  );
}